import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import type { FieldError } from 'react-hook-form';
import { cn } from '@/lib/utils';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: FieldError;
  containerClassName?: string;
  /**
   * 'default' — legacy 2px-radius input.
   * 'lms' — Figma "LMS Portal - Updated" input: N600 label with 6px gap,
   * rounded-8 Card/Stroke 3 border, 16/24 text, grey locked state.
   */
  variant?: 'default' | 'lms';
  /** Shows an in-input spinner while an async check (e.g. validation) runs. */
  loading?: boolean;
  /** Visually hide the label (kept for screen readers) — e.g. a repeated row. */
  hideLabel?: boolean;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      label,
      error,
      containerClassName = '',
      className = '',
      variant = 'default',
      loading = false,
      hideLabel = false,
      id,
      ...props
    },
    ref
  ) => {
    // Associate label and input so the label is clickable and the field is
    // reachable by its label text (assistive tech, getByLabelText).
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const labelClasses =
      variant === 'lms'
        ? 'block mb-1.5 text-body-regular text-neutral-n600'
        : 'block mb-1 text-body-regular text-character-title-85';

    const inputClasses =
      variant === 'lms'
        ? 'w-full outline-none font-roboto text-[16px] leading-6 text-black rounded-lg border border-lms-border bg-white px-4 py-3 focus:border-primary-6 transition-colors disabled:bg-[#f5f5f5] disabled:text-neutral-n400 disabled:cursor-not-allowed'
        : 'w-full outline-none text-body-regular text-character-title-85 rounded-[2px] border border-neutral-100 bg-neutral-0 px-3 py-2';

    return (
      <div className={containerClassName}>
        <label
          htmlFor={inputId}
          className={cn(labelClasses, hideLabel && 'sr-only')}
        >
          {label}
          {variant === 'lms' && props.required && (
            <span className="text-lms-red-hover"> *</span>
          )}
        </label>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            className={cn(inputClasses, loading && 'pr-10', className)}
            {...props}
          />
          {loading && (
            <div
              role="status"
              aria-label="Validating"
              className="pointer-events-none absolute inset-y-0 right-4 flex items-center"
            >
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-6" />
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-footnote-description text-error-500">
            {error.message}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
